import {
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Input,
  InputLabel,
  Paper,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { Position } from "../../../domain/models/position-model";
import ProductTable, { ProductTableRow } from "./components/products-table";
import NavBar from "../components/navbar";
import { useErrorHandler } from "../hooks";
import { LoadProductList } from "../../../domain/usecases/load-product-list";
import { Product } from "../../../domain/models/product-model";
import { TextMaskCustom } from "../Positions";
import { LoadPositionList } from "../../../domain/usecases/load-position-list";
import PositionsTable, { PositionTableRow } from "./components/positions-table";
import { CargoPrinterDialog } from "./components/cargo-printer-dialog";

type Props = {
  loadProducts: LoadProductList;
  loadPositions: LoadPositionList;
};

export default function LabelPrinter({ loadProducts, loadPositions }: Props) {
  const [positions, setPositions] = useState<Position[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [code, setCode] = useState("");
  const [positionCode, setPositionCode] = useState("");
  const [ean, setEan] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCargoPrinterOpen, setIsCargoPrinterOpen] = useState(false);

  const handleError = useErrorHandler(() => {
    alert("Não foi possível carregar os produtos");
  });

  const handleClick = async () => {
    if (!code && !ean && !description) {
      return alert("Preencha ao menos um campo");
    }
    setIsLoading(true);
    setPositions([]);
    try {
      const res = await loadProducts.load({
        code: ean,
        reference: code,
        description,
      });
      const unique: Product[] = [];
      res.forEach((product) => {
        const match = unique.find(
          (item) => item.reference === product.reference
        );
        if (!match) {
          unique.push(product);
        }
      });
      setProducts(unique);
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchPosition = async () => {
    const [street, building, level, apartment] = positionCode.split(".");
    const params: any = {
      includeInventory: false,
    };
    if (street || Number(street) === 0) {
      params.street = Number(street);
    }
    if (building || Number(building) === 0) {
      params.building = Number(building);
    }
    if (level || Number(level) === 0) {
      params.level = Number(level);
    }
    if (apartment || Number(apartment) === 0) {
      params.apartment = Number(apartment);
    }

    setIsLoading(true);
    setProducts([]);
    try {
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      const res = await loadPositions.load({
        warehouseId: warehouse!.id,
        filterOptions: params,
      });
      if (res) {
        const codes: string[] = [];
        res.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            codes.push(inventory!.item!.code);
          });
        });
      }
      setPositions(res);
    } catch (error) {
      console.log(error);
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CargoPrinterDialog
        open={isCargoPrinterOpen}
        setIsOpen={setIsCargoPrinterOpen}
      />
      <NavBar title="Imprimir Etiquetas" />
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextField
          label="Código Interno"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
        />
        <TextField
          label="Descrição"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
        <TextField
          label="EAN"
          value={ean}
          onChange={(e) => {
            setEan(e.target.value);
          }}
        />
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={handleClick}
        >
          Pesquisar
        </Button>
      </Divider>
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormControl>
          <InputLabel htmlFor="formatted-text-mask-input">Posição</InputLabel>
          <Input
            value={positionCode}
            onChange={(e: any) => {
              setPositionCode(e.target.value);
            }}
            name="textmask"
            id="formatted-text-mask-input"
            inputComponent={TextMaskCustom as any}
          />
        </FormControl>
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={handleSearchPosition}
        >
          Pesquisar
        </Button>
      </Divider>
      <Divider sx={{ mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setIsCargoPrinterOpen(true);
          }}
        >
          Imprimir Carga
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        <Paper>
          {products.length > 0 && (
            <ProductTable
              data={products.map(
                (product) =>
                  ({
                    id: product.reference,
                    reference: product.reference,
                    description: product.description,
                    ean: product.eanCode,
                  } as ProductTableRow)
              )}
            />
          )}
          {positions.length > 0 && (
            <PositionsTable
              data={positions.map(
                (position) =>
                  ({
                    id: position?.id?.toString(),
                    code: position.code,
                    position: `${
                      position.street.toString().length === 1
                        ? "0" + position.street.toString()
                        : position.street.toString()
                    }.${
                      position.building.toString().length === 1
                        ? "0" + position.building.toString()
                        : position.building.toString()
                    }${position.level ? "." + position.level.toString() : ""}${
                      position.apartment
                        ? "." + position.apartment.toString()
                        : ""
                    }`,
                  } as PositionTableRow)
              )}
            />
          )}
        </Paper>
      </Container>
    </>
  );
}
